import React, { useMemo, useState } from "react";
import Fuse from "fuse.js";
import { get, isEmpty, startCase, unionBy, xor } from "lodash";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { CheckboxDropdownItem } from "@sonder/ui/source/styled/fragments/CheckboxDropdownItem";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
  FilterTextArea,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { useSegment } from "analytics/segment";
import { setFilters } from "tasks/actions";
import { ENABLED_TASK_TYPE_OPTIONS } from "tasks/constants";
import { getTaskOptionByType } from "tasks/helpers";
import { useSegmentQueue } from "tasks/hooks";
import { useStore } from "tasks/store";
import { TypeEnum } from "typings/globalGQLTypes";

// Helpers
export const getTaskTypeMatches = (searchTerm: string) =>
  new Fuse(ENABLED_TASK_TYPE_OPTIONS, {
    shouldSort: true,
    threshold: 0.25,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    minMatchCharLength: 1,
    keys: [
      { name: "taskType", weight: 0.1 },
      { name: "displayName", weight: 0.9 },
      { name: "alternateNames", weight: 0.3 },
    ],
  }).search(searchTerm.trim());

// Components
interface TaskTypeSearchItemProps {
  taskType: TypeEnum;
}
export const TaskTypeSearchItem = ({ taskType }: TaskTypeSearchItemProps) => {
  const { state, dispatch } = useStore();
  const { filters: globalFilters } = state;
  const selectedTaskTypes = get(globalFilters, ["taskType"]) || [];
  const pushToSegmentQueue = useSegmentQueue(globalFilters);
  const taskTypeOption = getTaskOptionByType(taskType);

  if (!taskTypeOption) return null;
  const isChecked = selectedTaskTypes.includes(taskType);

  return (
    <CheckboxDropdownItem
      checked={isChecked}
      onClick={() => {
        const newTaskTypes = xor(selectedTaskTypes, [taskType]);
        dispatch(setFilters({ taskType: newTaskTypes }));
        pushToSegmentQueue({
          eventName: "tasktype_filter",
          action: "click",
          element: `tasktype_filter-${taskType}`,
          props: { nextValue: !isChecked ? "selected" : "deselected" },
        });
      }}
    >
      {startCase(taskTypeOption.displayName)}
    </CheckboxDropdownItem>
  );
};

const ClearTypeFiltersButton = () => {
  const { dispatch } = useStore();
  const onClick = () =>
    requestAnimationFrame(() => dispatch(setFilters({ taskType: [] })));

  return <Link onClick={onClick}>Clear selected</Link>;
};

export const TaskTypeFilters = () => {
  const { track } = useSegment();
  const {
    state: { filters: globalFilters },
  } = useStore();
  const [search, setSearch] = useState("");

  const selectedTaskTypes = get(globalFilters, ["taskType"]) || [];

  const matchedTaskTypes = useMemo(() => {
    const matches = isEmpty(search)
      ? ENABLED_TASK_TYPE_OPTIONS
      : getTaskTypeMatches(search);
    const selected = ENABLED_TASK_TYPE_OPTIONS.filter(({ taskType }) =>
      selectedTaskTypes.includes(taskType),
    );
    return unionBy(selected, matches, option => option.taskType).map(
      ({ taskType }) => taskType,
    );
  }, [search, selectedTaskTypes]);

  const totalSelected = selectedTaskTypes.length;

  return (
    <Dropdown
      trigger={
        <Button fullWidth iconRight type="button">
          Task Type
          {totalSelected > 0 && ` (${totalSelected})`}
          <ChevronDown />
        </Button>
      }
      onClose={() => {
        track({
          eventName: "tasktype_filter",
          action: "click",
          element: "tasktype_filter.dropdown_trigger",
          props: { nextValue: "closed" },
        });
      }}
      onOpen={() => {
        track({
          eventName: "tasktype_filter",
          action: "click",
          element: "tasktype_filter.dropdown_trigger",
          props: { nextValue: "opened" },
        });
      }}
    >
      {({ isOpen: isPortalOpen }) =>
        isPortalOpen && (
          <FilterDropdownContent align="center">
            <FilterTextArea
              autoFocus
              placeholder="Search task types"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <FilterButtonList>
              {matchedTaskTypes.map(taskType => (
                <TaskTypeSearchItem key={taskType} taskType={taskType} />
              ))}
            </FilterButtonList>
            {matchedTaskTypes.length === 0 && (
              <FilterEmpty>No matches found</FilterEmpty>
            )}
            <FilterFooter>
              {`${totalSelected} selected`}
              {totalSelected > 0 && <ClearTypeFiltersButton />}
            </FilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
